import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';

import { EmbeddedSwitcher } from '../../../chrome/entity/EntitySwitcher';
import { Hint } from '../../../chrome/hint';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';
import { AccountAvatar, CoauthorStack } from '../media/AccountAvatar';
import { MediaGallery } from '../media/MediaView';
import type { PostReach, PostRow } from '../api/schemas';
import { PostMenu, type CardMenu } from '../shared/PostMenu';
import { Metric } from '../publications/cells';
import type { PostFigures, Reading } from '../publications/readings';
import { dateTime, splitCaption } from './format';
import { kindLabel, networkLabel, pub } from './messages';

const HEADER_METRICS: readonly (keyof PostFigures)[] = [
  'reach',
  'estimatedReach',
  'likes',
  'comments',
  'shares',
  'saves',
];

/**
 * The post header as one card: the thumbnail beside the byline, a heading
 * drawn from the caption's first line with the rest as a quoted excerpt, and
 * an outline link out to the network the post came from. The relatedness
 * chip reads the effective flag the figures carry, override included.
 */
export function PostCard(props: {
  post: PostRow;
  figures?: PostReach | null;
  /** The latest reading of each metric, drawn beside its glyph; a metric the
   *  post has no reading of is left out. */
  metrics?: Reading<PostFigures>;
  menu?: CardMenu;
}): ReactNode {
  const { post, figures } = props;
  const related = figures?.related ?? post.related;
  const author = post.label ?? post.handle;
  const { heading, excerpt } = splitCaption(post.caption);

  return (
    <Card className="overflow-hidden">
      <div className="flex flex-col gap-4 px-4 py-4 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="flex min-w-0 flex-col gap-4 sm:flex-1 sm:flex-row">
          <div className="w-full max-w-64 shrink-0 sm:w-40">
            <MediaGallery
              media={post.media}
              aspect={post.kind === 'REEL' ? '9/16' : '4/5'}
              alt={pub('post.mediaAlt')}
            />
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <AccountAvatar size="sm" avatarUrl={post.avatar_url} name={author} />
              <span className="text-body-sm truncate font-medium">
                {author ?? pub('post.authorAbsent')}
              </span>
              <CoauthorStack coauthors={post.coauthors} />
              <span className="text-caption text-muted-foreground truncate">
                {[
                  post.label !== null ? post.handle : null,
                  networkLabel(post.platform),
                  kindLabel(post.kind),
                ]
                  .filter((part) => part !== null)
                  .join(' · ')}
              </span>
              <span data-numeric className="text-caption text-muted-foreground">
                {dateTime(post.published_at)}
              </span>
              {post.deleted_at !== null && <Badge variant="outline">{pub('post.deleted')}</Badge>}
            </div>
            <h1 className="text-title-md min-w-0">
              <EmbeddedSwitcher>
                <span className="line-clamp-2 min-w-0">{heading ?? pub('post.untitled')}</span>
              </EmbeddedSwitcher>
            </h1>
            {excerpt !== null && (
              <p className="text-body-sm text-muted-foreground">
                {pub('post.excerpt', { text: excerpt })}
              </p>
            )}
            {props.metrics !== undefined && (
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                {HEADER_METRICS.map((metric) => (
                  <Metric key={metric} figures={props.metrics!} metric={metric} />
                ))}
              </div>
            )}
            {related && (
              <div className="flex items-center gap-1">
                <Badge
                  variant="secondary"
                  className="bg-primary-quiet text-primary-quiet-foreground"
                >
                  {pub('post.related')}
                </Badge>
                {figures?.related_provenance === 'overridden' && (
                  <Hint label={pub('post.relatedAbout')}>{pub('post.relatedManual')}</Hint>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          {post.url !== null && (
            <Button asChild variant="outline" size="sm">
              <a href={post.url} target="_blank" rel="noopener">
                {pub('post.viewOn', { network: networkLabel(post.platform) })}
                <ExternalLink aria-hidden className="size-4" />
              </a>
            </Button>
          )}
          {props.menu !== undefined && (
            <PostMenu
              post={post}
              campaignId={props.menu.campaignId}
              tenantId={props.menu.tenantId}
              related={related}
              canReclassify={props.menu.canReclassify}
            />
          )}
        </div>
      </div>
    </Card>
  );
}
